import { useGetSystemOverviewQuery } from '../store/adminApi';
import { Card, StatCard } from '../components/Card';
import { LoadingState, ErrorState } from '../components/LoadingSpinner';
import { StatusBadge } from '../components/StatusBadge';

export function OverviewPage() {
  const { data, isLoading, error } = useGetSystemOverviewQuery(undefined, {
    pollingInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return <LoadingState message="Loading system overview..." />;
  }

  if (error || !data) {
    return <ErrorState message="Failed to load system overview" />;
  }

  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">System Overview</h2>
        <p className="text-sm text-gray-500">Last updated: {formatTime(data.time)}</p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Database"
          value={data.dbOk ? 'Connected' : 'Disconnected'}
          status={data.dbOk ? 'success' : 'error'}
        />
        <StatCard
          label="APNs (iOS)"
          value={data.apnsConfigured ? 'Configured' : 'Not Configured'}
          status={data.apnsConfigured ? 'success' : 'warning'}
        />
        <StatCard
          label="FCM (Android)"
          value={data.fcmConfigured ? 'Configured' : 'Not Configured'}
          status={data.fcmConfigured ? 'success' : 'warning'}
        />
        <StatCard
          label="Device Tokens"
          value={data.counts.deviceTokensTotal.toLocaleString()}
          status="neutral"
        />
      </div>

      {/* 24h Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          label="Delivery Attempts (24h)"
          value={data.counts.deliveryAttempts24h.toLocaleString()}
          status="neutral"
        />
        <StatCard
          label="Failed Deliveries (24h)"
          value={data.counts.deliveryFailed24h.toLocaleString()}
          status={data.counts.deliveryFailed24h > 0 ? 'warning' : 'success'}
        />
        <StatCard
          label="Success Rate (24h)"
          value={
            data.counts.deliveryAttempts24h > 0
              ? `${(
                  ((data.counts.deliveryAttempts24h - data.counts.deliveryFailed24h) /
                    data.counts.deliveryAttempts24h) *
                  100
                ).toFixed(1)}%`
              : 'N/A'
          }
          status={
            data.counts.deliveryAttempts24h === 0
              ? 'neutral'
              : data.counts.deliveryFailed24h / data.counts.deliveryAttempts24h < 0.05
              ? 'success'
              : 'warning'
          }
        />
      </div>

      {/* Recent Errors */}
      <Card title="Recent Errors (24h)">
        {data.recentErrors.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">
            No errors in the last 24 hours
          </p>
        ) : (
          <div className="space-y-3">
            {data.recentErrors.map((err, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 p-3 bg-gray-50 rounded-md"
              >
                <StatusBadge
                  status={err.provider === 'apns' ? 'info' : 'success'}
                  label={err.provider.toUpperCase()}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {err.errorCode && (
                      <span className="text-sm font-mono text-red-600">
                        {err.errorCode}
                      </span>
                    )}
                    {err.appId && (
                      <span className="text-xs text-gray-500">App: {err.appId}</span>
                    )}
                  </div>
                  {err.errorMessage && (
                    <p className="text-sm text-gray-600 mt-1 truncate">
                      {err.errorMessage}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {formatTime(err.createdAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
